/**
 * useRemoteCommands hook
 * 
 * Escuta comandos remotos enviados do dashboard para o player.
 * Suporta:
 * - WIPE_DATA: Limpa todo o storage local do player e reinicia
 * 
 * Compatibilidade: Todos os navegadores modernos
 * - Chrome 40+, Firefox 41+, Safari 11.1+, Edge 79+
 */

import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { clearPlayerCache } from '@/utils/storageUtils';

interface UseRemoteCommandsProps {
    deviceId?: string | null;
}

export const useRemoteCommands = ({ deviceId }: UseRemoteCommandsProps = {}) => {

    /**
     * Executa o comando WIPE_DATA - limpa todo o storage do player
     */
    const executeWipeData = useCallback(async (commandId: string) => {
        console.log('🔴 [REMOTE_CMD] Executando Reset Remoto de Storage...');

        try {
            // 1. Backup de credenciais essenciais (manter vínculo com conta)
            const deviceIdBackup = localStorage.getItem('device_id');
            const deviceCodeBackup = localStorage.getItem('deviceCode');
            const deviceTokenBackup = localStorage.getItem('device_token');

            // 2. Limpar LocalStorage (usando função existente + limpeza extra)
            clearPlayerCache();

            // Limpar keys adicionais que clearPlayerCache não cobre
            const additionalKeysToRemove = [
                'player_screens',
                'player_medias',
                'pending_media_events',
                'lastPromoCheck',
                'news_cache',
                'news_cache_time',
            ];

            additionalKeysToRemove.forEach(key => {
                try {
                    localStorage.removeItem(key);
                } catch (e) {
                    console.warn(`[REMOTE_CMD] Falha ao remover key: ${key}`);
                }
            });

            // Restaurar credenciais essenciais
            if (deviceIdBackup) localStorage.setItem('device_id', deviceIdBackup);
            if (deviceCodeBackup) localStorage.setItem('deviceCode', deviceCodeBackup);
            if (deviceTokenBackup) localStorage.setItem('device_token', deviceTokenBackup);

            // 3. Limpar SessionStorage
            try {
                sessionStorage.clear();
                console.log('✅ [REMOTE_CMD] SessionStorage limpo');
            } catch (e) {
                console.warn('[REMOTE_CMD] Falha ao limpar SessionStorage');
            }

            // 4. Limpar Cache Storage (PWA - vídeos/imagens em cache)
            if ('caches' in window) {
                try {
                    const cacheKeys = await caches.keys();
                    await Promise.all(cacheKeys.map(key => caches.delete(key)));
                    console.log(`✅ [REMOTE_CMD] ${cacheKeys.length} caches apagados`);
                } catch (e) {
                    console.warn('[REMOTE_CMD] Falha ao limpar Cache Storage:', e);
                }
            }

            // 5. Limpar IndexedDB (bancos locais conhecidos)
            const knownDatabases = [
                'watch_videos',
                'watch_media',
                'watch_playlists',
                'watch_content',
                'keyval-store',
                'workbox-precache',
            ];

            for (const dbName of knownDatabases) {
                try {
                    const deleteRequest = window.indexedDB.deleteDatabase(dbName);
                    await new Promise<void>((resolve, reject) => {
                        deleteRequest.onsuccess = () => resolve();
                        deleteRequest.onerror = () => reject(deleteRequest.error);
                        deleteRequest.onblocked = () => resolve(); // Continuar mesmo se bloqueado
                    });
                    console.log(`✅ [REMOTE_CMD] IndexedDB "${dbName}" apagado`);
                } catch (err) {
                    console.warn(`⚠️ [REMOTE_CMD] Não foi possível apagar "${dbName}":`, err);
                }
            }

            // 6. Desregistrar Service Workers
            if ('serviceWorker' in navigator) {
                try {
                    const registrations = await navigator.serviceWorker.getRegistrations();
                    for (const registration of registrations) {
                        await registration.unregister();
                        console.log('✅ [REMOTE_CMD] Service Worker desregistrado');
                    }
                } catch (e) {
                    console.warn('[REMOTE_CMD] Falha ao desregistrar Service Workers:', e);
                }
            }

            // 7. Marcar comando como executado no Supabase
            try {
                const { error } = await supabase
                    .from('device_commands')
                    .update({
                        status: 'EXECUTED',
                        executed_at: new Date().toISOString(),
                        metadata: {
                            execution_log: 'Storage limpo com sucesso',
                            executed_at_client: new Date().toISOString()
                        }
                    })
                    .eq('id', commandId);

                if (error) {
                    console.warn('[REMOTE_CMD] Erro ao marcar comando como executado:', error);
                }
            } catch (e) {
                console.warn('[REMOTE_CMD] Falha ao atualizar status do comando:', e);
            }

            console.log('✅ [REMOTE_CMD] Reset completo! Reiniciando página...');

            // Emitir evento para atualizar UI instantaneamente (antes do reload)
            window.dispatchEvent(new Event('remoteStorageReset'));

            // 8. Reiniciar página (hard reload)
            setTimeout(() => {
                window.location.href = window.location.origin + window.location.pathname + '?reset=true&t=' + Date.now();
            }, 500);

        } catch (error) {
            console.error('❌ [REMOTE_CMD] Erro ao executar wipe:', error);

            // Marcar como falho
            try {
                await supabase
                    .from('device_commands')
                    .update({
                        status: 'FAILED',
                        metadata: {
                            error: String(error)
                        }
                    })
                    .eq('id', commandId);
            } catch (e) {
                console.warn('[REMOTE_CMD] Falha ao marcar comando como falho');
            }
        }
    }, []);

    useEffect(() => {
        if (!deviceId) {
            console.warn('⚠️ [REMOTE_CMD] Device ID não encontrado. Listener de comandos não iniciado.');
            return;
        }

        console.log(`👂 [REMOTE_CMD] Escutando comandos remotos para device: ${deviceId}`);

        // Verificar comandos pendentes ao iniciar
        const checkPendingCommands = async () => {
            try {
                const { data: pendingCommands, error } = await supabase
                    .from('device_commands')
                    .select('*')
                    .eq('device_id', deviceId)
                    .eq('status', 'PENDING')
                    .gt('expires_at', new Date().toISOString())
                    .order('created_at', { ascending: true });

                if (error) {
                    console.warn('[REMOTE_CMD] Erro ao buscar comandos pendentes:', error);
                    return;
                }

                if (pendingCommands && pendingCommands.length > 0) {
                    console.log(`📋 [REMOTE_CMD] ${pendingCommands.length} comandos pendentes encontrados`);
                    const firstCommand = pendingCommands[0];

                    if (firstCommand.type === 'WIPE_DATA') {
                        executeWipeData(firstCommand.id);
                    }
                }
            } catch (e) {
                console.warn('[REMOTE_CMD] Erro ao verificar comandos pendentes:', e);
            }
        };

        checkPendingCommands();

        // Subscrição Realtime para novos comandos
        const channel = supabase
            .channel(`device_commands:${deviceId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'device_commands',
                    filter: `device_id=eq.${deviceId}`
                },
                (payload) => {
                    console.log('📡 [REMOTE_CMD] Comando recebido:', payload.new);

                    const command = payload.new as {
                        id: string;
                        type: string;
                        status: string;
                    };

                    if (command.type === 'WIPE_DATA' && command.status === 'PENDING') {
                        executeWipeData(command.id);
                    }
                }
            )
            .subscribe((status) => {
                console.log('📡 [REMOTE_CMD] Status da subscrição:', status);
            });

        // Cleanup ao desmontar
        return () => {
            console.log('👋 [REMOTE_CMD] Desconectando listener de comandos');
            supabase.removeChannel(channel);
        };
    }, [deviceId, executeWipeData]);

    return null;
};

export default useRemoteCommands;
